import type { TokenPair, TokenStorageStrategy } from "../types";
import { LocalStorageStrategy } from "./storage/localStorage";

type TokensChangedCallback = (tokens: TokenPair | null) => void;

class SessionCore {
  private strategy: TokenStorageStrategy;
  private onTokensChanged: TokensChangedCallback | null = null;

  constructor(strategy?: TokenStorageStrategy) {
    this.strategy = strategy ?? new LocalStorageStrategy();
  }

  /** 同步读取当前 tokens（冷启动判定用） */
  getTokens(): TokenPair | null {
    return this.strategy.getTokens();
  }

  /** 写入 tokens（登录成功/刷新成功后调用） */
  setTokens(tokens: TokenPair): void {
    this.strategy.setTokens(tokens);
    this.onTokensChanged?.(tokens);
  }

  /** 清除 tokens（登出时调用） */
  clear(): void {
    this.strategy.clear();
    this.onTokensChanged?.(null);
  }

  /** 注册变更监听（Auth Store 订阅用） */
  subscribe(callback: TokensChangedCallback): () => void {
    this.onTokensChanged = callback;
    return () => {
      this.onTokensChanged = null;
    };
  }

  /** 判定 token 是否即将过期 */
  isExpiringSoon(thresholdMs: number): boolean {
    const tokens = this.getTokens();
    if (!tokens) {
      return false;
    }
    return Date.now() >= tokens.expiresAt - thresholdMs;
  }

  /** 判定是否有有效 session（冷启动用，同步） */
  hasValidSession(): boolean {
    const tokens = this.getTokens();
    if (!tokens?.accessToken) {
      return false;
    }
    return tokens.expiresAt > Date.now();
  }

  /** 判定是否可刷新（有 refreshToken） */
  canRefresh(): boolean {
    const tokens = this.getTokens();
    return !!tokens?.refreshToken;
  }

  /** 切换存储策略（迁移时用） */
  migrateStorage(newStrategy: TokenStorageStrategy): void {
    const current = this.strategy.getTokens();
    this.strategy = newStrategy;
    if (current) {
      newStrategy.setTokens(current);
    }
  }
}

export const sessionCore = new SessionCore();
export { SessionCore };
